import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate, useParams } from 'react-router-dom';
import './PublicClinicReviews.css';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, ChartTooltip, Legend);

const PublicClinicReviews = () => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [clinicName, setClinicName] = useState('');
  const navigate = useNavigate();
  const { clinicId: routeParam } = useParams();

  useEffect(() => {
    const fetchReviews = async () => {
      if (!routeParam) return;

      let realClinicId = '';

      // Try interpreting the routeParam as a token first
      const clinicSnap = await getDocs(
        query(
          collection(db, 'clinics'),
          where('token', '==', routeParam),
          where('is_active', '==', true)
        )
      );

      if (!clinicSnap.empty) {
        const clinicDoc = clinicSnap.docs[0];
        realClinicId = clinicDoc.id;
        setClinicName(clinicDoc.data().name || '');
      } else {
        // Fallback to using routeParam as actual Firestore doc ID
        realClinicId = routeParam;
        const fallbackSnap = await getDocs(
          query(
            collection(db, 'clinics'),
            where('__name__', '==', realClinicId),
            where('is_active', '==', true)
          )
        );
        if (!fallbackSnap.empty) {
          const fallbackDoc = fallbackSnap.docs[0];
          setClinicName(fallbackDoc.data().name || '');
        } else {
          console.warn('No clinic found with this token or ID.');
          return;
        }
      }

      // Fetch reviews by clinic_id
      const reviewSnap = await getDocs(
        query(collection(db, 'clinic_reviews'), where('clinic_id', '==', realClinicId))
      );
      const data = reviewSnap.docs.map(doc => doc.data());
      setReviews(data);
    };

    fetchReviews();
  }, [routeParam]);

  const aggregateSentiment = () => {
    const sentimentCount = { positive: 0, negative: 0, neutral: 0 };
    reviews.forEach(r => {
      if (sentimentCount[r.feeling] !== undefined) {
        sentimentCount[r.feeling]++;
      }
    });
    return sentimentCount;
  };

  const sentimentData = aggregateSentiment();
  const pieData = {
    labels: [
      t('review_feeling_positive'),
      t('review_feeling_negative'),
      t('review_feeling_neutral'),
    ],
    datasets: [
      {
        data: [
          sentimentData.positive,
          sentimentData.negative,
          sentimentData.neutral,
        ],
        backgroundColor: ['#4caf50', '#f44336', '#ffc107'],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="public-clinic-reviews-container">
      <h2 className="title">{t('clinicReviewPage.title')} - {clinicName}</h2>

      <div className="chart-wrapper">
        <h4>{t('clinicReviewPage.sentimentChart')}</h4>
        <div style={{ width: '100%', maxWidth: 400, margin: '0 auto' }}>
          <Pie data={pieData} key={JSON.stringify(pieData)} />
        </div>
      </div>

      <button className="export-btn1" onClick={() => navigate(-1)}>
        ⬅️ Back
      </button>

      <h4 className="recent-title">{t('clinicReviewPage.recentReviews')}</h4>
      <div className="reviews-list">
        {reviews.length === 0 ? (
          <p>{t('clinicReviewPage.noReviews')}</p>
        ) : (
          reviews.slice(0, 10).map((r, idx) => (
            <div key={idx} className="review-card">
              <p>
                <strong>⭐ {r.rating}</strong> — {t(`review_feeling_${r.feeling}`)}
              </p>
              {r.comment && <p>{r.comment}</p>}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PublicClinicReviews;
